import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiGet } from "../../api/client";
import type { TutorDashboard, InProgressRow, RosterEntry } from "../../types";

export function TutorDashboardPage() {
  const [downloadNotice, setDownloadNotice] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["tutor-dashboard"],
    queryFn: () => apiGet<TutorDashboard>("/api/tutor/dashboard"),
  });

  const { data: roster } = useQuery({
    queryKey: ["tutor-roster"],
    queryFn: () => apiGet<RosterEntry[]>("/api/tutor/learners"),
  });

  const live = useQuery({
    queryKey: ["tutor-in-progress"],
    queryFn: () =>
      apiGet<{ in_progress: InProgressRow[]; active_window_minutes: number; abandoned_count: number }>(
        "/api/tutor/in-progress"
      ),
    refetchInterval: 15000,
  });

  const handleExportCsv = () => {
    if (!roster || roster.length === 0) return;
    const headers = [
      "Student Number",
      "Full Name",
      "Subject Curriculum",
      "Mastery Percentage",
      "Course Completion",
      "Total Attempts",
      "Status",
    ];

    const rows = roster.map((r) => {
      return [
        `"${r.student_number || "N/A"}"`,
        `"${r.name.replace(/"/g, '""')}"`,
        `"${r.curriculum.replace(/"/g, '""')}"`,
        `"${r.mastery}%"`,
        `"${r.completion}%"`,
        r.attempts,
        `"${r.needs_intervention ? "Intervention Needed" : "On Track"}"`,
      ].join(",");
    });

    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `PyDialog_Cohort_Progress_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setDownloadNotice("Cohort progress exported as CSV.");
    setTimeout(() => setDownloadNotice(""), 4000);
  };

  if (isLoading) return <p className="text-muted">Loading teaching dashboard...</p>;
  if (!data) return <p>No dashboard data available.</p>;

  return (
    <div className="tutor-dashboard">
      <header className="tutor-header-row">
        <div>
          <h1>Teaching Overview &amp; Diagnostics</h1>
          <p className="text-muted">
            Your cohort's live dialogues, mastery, and the objectives they're struggling with.
          </p>
        </div>

        <div className="tutor-header-actions">
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleExportCsv}>
            📊 Export Cohort Progress (CSV)
          </button>
        </div>
      </header>

      {downloadNotice && (
        <div className="notice" role="status">
          {downloadNotice}
        </div>
      )}

      {/* Live activity for this instructor's cohort */}
      <div className="card live-dialogues-card">
        <div className="card-header-badge-row">
          <h3>🟢 Active Dialogues in Progress</h3>
          <span className="live-pulse-badge">Live Auto-refresh (15s)</span>
        </div>

        {live.isLoading ? (
          <p className="text-muted">Loading live activity…</p>
        ) : !live.data || live.data.in_progress.length === 0 ? (
          <p className="text-muted" style={{ margin: "8px 0" }}>
            No learners in your cohort are currently in an active dialogue.
          </p>
        ) : (
          <div className="report-scroll">
            <table>
              <thead>
                <tr>
                  <th>Learner</th>
                  <th>Lesson Unit</th>
                  <th>Progress</th>
                  <th>Elapsed</th>
                  <th>Idle Status</th>
                </tr>
              </thead>
              <tbody>
                {live.data.in_progress.map((r) => (
                  <tr key={r.session_id}>
                    <td>
                      <strong>{r.learner}</strong>
                    </td>
                    <td>{r.lesson_title ?? r.lesson_id}</td>
                    <td>
                      Question {r.answered} of {r.total_questions}
                    </td>
                    <td>{r.elapsed_minutes != null ? `${r.elapsed_minutes} min` : "—"}</td>
                    <td>{r.idle_minutes != null ? `${r.idle_minutes} min` : "Active"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats cards */}
      <div className="roadmap-metrics-grid">
        <div className="stat-card">
          <span className="stat-value">{data.stats.total_learners}</span>
          <span className="stat-label">Active Learners</span>
          <span className="stat-subtext">Cohort enrollment</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{data.stats.avg_completion}%</span>
          <span className="stat-label">Average Completion</span>
          <span className="stat-subtext">Syllabus progression</span>
        </div>
        <div className="stat-card">
          <span className="stat-value text-success">{data.stats.avg_mastery}%</span>
          <span className="stat-label">Cohort Mastery</span>
          <span className="stat-subtext">Assessed understanding</span>
        </div>
        <div className="stat-card">
          <span className="stat-value text-danger">{data.stats.at_risk}</span>
          <span className="stat-label">At Risk Learners</span>
          <span className="stat-subtext">Flagged for intervention</span>
        </div>
      </div>

      {/* Objective Heatmap */}
      {data.heatmap.length > 0 && (
        <div className="card" style={{ marginBottom: 24, padding: "24px" }}>
          <h3>🔥 Objective Heatmap</h3>
          <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: 16 }}>
            Average score on each objective your learners have attempted, lowest first.
          </p>
          <div className="heatmap-list">
            {data.heatmap.map((h, i) => (
              <div key={i} className="heatmap-row">
                <span className="heatmap-desc">{h.description}</span>
                <div className="heatmap-bar-wrap">
                  <div
                    className={`heatmap-bar ${
                      h.avg < 50 ? "bar-danger" : h.avg < 70 ? "bar-warning" : "bar-ok"
                    }`}
                    style={{ width: `${h.avg}%` }}
                  />
                </div>
                <span className="heatmap-pct">{h.avg}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Interventions */}
      {data.interventions.length > 0 && (
        <div className="card report-section-flagged" style={{ marginBottom: 24, padding: "24px" }}>
          <h3>🚨 Priority Learner Interventions</h3>
          <p className="text-muted" style={{ fontSize: "0.85rem", marginBottom: 14 }}>
            Learners struggling on several objectives who may need individual support.
          </p>
          <ul className="intervention-list">
            {data.interventions.map((int, i) => (
              <li key={i} className="intervention-item">
                <div className="intervention-top">
                  <strong>{int.learner_name}</strong>
                  <span className="text-muted">
                    {" "}— requires assistance on {int.struggling_count} objectives
                  </span>
                </div>
                <div className="intervention-objs" style={{ marginTop: 6 }}>
                  {int.objectives.map((o, j) => (
                    <span key={j} className="badge badge-warning" style={{ marginRight: 6 }}>
                      {o.label} ({Math.round(o.score * 100)}%)
                    </span>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Learner Roster Summary */}
      <div className="card" style={{ padding: "24px" }}>
        <div className="card-header-badge-row">
          <h3>Cohort Roster</h3>
          <Link to="/tutor/learners" className="btn btn-sm btn-secondary">
            View Detailed Roster Table →
          </Link>
        </div>

        {roster && roster.length > 0 ? (
          <div className="report-scroll" style={{ marginTop: 14 }}>
            <table>
              <thead>
                <tr>
                  <th>Student Number</th>
                  <th>Name</th>
                  <th>Curriculum</th>
                  <th>Mastery</th>
                  <th>Completion</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {roster.slice(0, 5).map((l) => (
                  <tr key={l.id}>
                    <td>
                      <span className="student-number">{l.student_number || "—"}</span>
                    </td>
                    <td>
                      <strong>{l.name}</strong>
                    </td>
                    <td>{l.curriculum}</td>
                    <td>
                      <span
                        style={{
                          fontWeight: 700,
                          color:
                            l.mastery >= 70
                              ? "var(--success)"
                              : l.mastery >= 50
                              ? "var(--warning)"
                              : "var(--mark)",
                        }}
                      >
                        {l.mastery}%
                      </span>
                    </td>
                    <td>{l.completion}%</td>
                    <td>
                      {l.needs_intervention ? (
                        <span className="badge badge-danger">Needs Help</span>
                      ) : (
                        <span className="badge badge-success">On Track</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted">No learners registered in your cohort yet.</p>
        )}
      </div>
    </div>
  );
}
